/**
 * Playwright client diagnostics helpers.
 *
 * @module playwright-client/diagnostics
 */

import type { CapturedNetworkRequest } from './internal-types.js';

/** Noise URL patterns to exclude from pending network diagnostics. */
export const NETWORK_NOISE_URL_PATTERNS: readonly RegExp[] = [
  /google-analytics\.com/,
  /googletagmanager\.com/,
  /facebook\.net/,
  /hotjar\.com/,
  /sentry\.io/,
  /segment\.io/,
  /mixpanel\.com/,
  /\/analytics/i,
  /\/tracking/i,
  /\/pixel/i,
  /favicon\.ico/,
  /^data:/,
  /\.gif\?/, // tracking pixels
];

export function getPendingNetworkInfo(
  requests: Iterable<CapturedNetworkRequest>,
  opts: { noisePatterns?: readonly RegExp[] } = {}
): { pendingCount: number; sampleUrls: string[] } {
  const noisePatterns = opts.noisePatterns ?? NETWORK_NOISE_URL_PATTERNS;
  const pendingRequests: string[] = [];

  for (const req of requests) {
    if (req.status !== 'pending') continue;

    const isNoise = noisePatterns.some((pattern) => pattern.test(req.url));
    if (!isNoise) pendingRequests.push(req.url);
  }

  return {
    pendingCount: pendingRequests.length,
    sampleUrls: pendingRequests.slice(0, 3).map(formatNetworkSampleUrl),
  };
}

export function formatNetworkRequests(requests: Iterable<CapturedNetworkRequest>): string {
  const lines: string[] = [];
  for (const req of requests) {
    lines.push(`[${req.status}] ${req.method} ${req.url}`);
  }
  return lines.join('\n');
}

function formatNetworkSampleUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return truncateSampleUrl(parsed.pathname + parsed.search);
  } catch {
    return truncateSampleUrl(url);
  }
}

function truncateSampleUrl(value: string): string {
  return value.length > 60 ? `${value.slice(0, 57)}...` : value;
}
